import React, { useState, useEffect } from 'react';

const WidgetDashboard = ({ currentUser, onEditWidget, onCreateNew }) => {
  const [widgets, setWidgets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (currentUser?.id) {
      loadUserWidgets();
    }
  }, [currentUser]);

  const loadUserWidgets = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`https://app.defindly.com/api/get-user-widgets.php?userId=${currentUser.id}`);
      const data = await response.json();
      
      if (data.success) {
        setWidgets(data.widgets || []);
      } else {
        console.error('Failed to load widgets:', data.error);
      }
    } catch (error) {
      console.error('Error loading widgets:', error);
    }
    setIsLoading(false);
  };

  const deleteWidget = async (widgetId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this widget? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch('https://app.defindly.com/api/delete-widget.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          id: widgetId, 
          userId: currentUser.id 
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setWidgets(widgets.filter(w => w.id !== widgetId));
        alert('Widget deleted successfully');
      } else {
        alert('Failed to delete widget: ' + result.error);
      }
    } catch (error) {
      console.error('Error deleting widget:', error);
      alert('Failed to delete widget');
    }
  };

  const copyEmbedCode = (widgetId) => {
    const embedCode = `<script src="https://app.defindly.com/embed/widget-embed.js" data-widget-id="${widgetId}"></script>`;
    navigator.clipboard.writeText(embedCode);
    alert('Embed code copied to clipboard!');
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="text-center py-12">
          <div className="text-2xl mb-4">Loading your widgets...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="page-title">My Widgets</h1>
          <p className="page-subtitle">Manage your chat widgets</p>
        </div>
        <button className="btn-primary" onClick={onCreateNew}>
          + Create New Widget
        </button>
      </div>

      {widgets.length === 0 ? (
        <div className="card p-12 text-center">
          <h3 className="text-xl font-semibold mb-2">No widgets yet</h3>
          <p className="text-gray-600 mb-6">
            Create your first widget to get started with AI chat on your website
          </p>
          <button className="btn-primary" onClick={onCreateNew}>
            Create Your First Widget
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {widgets.map(widget => (
            <div 
              key={widget.id} 
              className="card hover:shadow-md transition-all"
              style={{
                padding: '20px 24px',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                background: 'white'
              }}
            >
              <div className="flex items-center justify-between gap-6">
                {/* Left: Widget Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-4">
                    <span className="text-base text-gray-900">
                      {widget.config?.botName || 'Unnamed Widget'}
                    </span>
                    <span className="text-base text-gray-500">
                      {new Date(widget.createdAt).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </span>
                  </div>
                </div>

                {/* Right: Action Buttons */}
                <div className="flex items-center gap-3 flex-shrink-0">
                  <button
                    onClick={() => onEditWidget(widget.id)}
                    style={{
                      padding: '10px 24px',
                      background: 'linear-gradient(135deg, #06ADED, #38BFEF)',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      fontWeight: '500',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    Edit
                  </button>
                  
                  <button
                    onClick={() => copyEmbedCode(widget.id)}
                    style={{
                      padding: '10px 24px',
                      background: 'linear-gradient(135deg, #06ADED, #38BFEF)',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      fontWeight: '500',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    Copy Code
                  </button>
                  
                  <button
                    onClick={() => deleteWidget(widget.id)}
                    style={{
                      padding: '10px 24px',
                      background: '#ef4444',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      fontWeight: '500',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WidgetDashboard;